import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, Chip, Button, Divider, CircularProgress, Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ApexCharts from 'apexcharts';
import { Person } from '@mui/icons-material';
import Graph from "react-vis-network-graph";
import Navbar from '../../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../../utils/axiosInstance';
import config from '../../../../config.json';
import url from '../../../../url.json';

export default function ClassApplicationAnalyticsDashboard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  
  // 課中即時資料
  const [realtimeData, setRealtimeData] = useState({
    pendingQuestions: { new: 0, toCategorize: 0, toReply: 0 },
    stickyHot: []
  });
  
  // 課後診斷資料
  const [postClassData, setPostClassData] = useState({
    participation: [],
    tasks: [],
    crossGroupViewStats: {
      studentViewCounts: [],
      groupViewInCounts: []
    }
  });
  const [postLastUpdatedAt, setPostLastUpdatedAt] = useState(null);
  
  // 小組互動網絡圖資料
  const [interactionNetworks, setInteractionNetworks] = useState({});
  const [selectedGroupForNetwork, setSelectedGroupForNetwork] = useState(null);
  const [courseDispatch, setCourseDispatch] = useState(null); // 課程派發資訊

  // 圖表資料
  const [chartNodes, setChartNodes] = useState([]);

  // WebSocket 追蹤（已移除在線監聽相關）
  const studentListRef = useRef([]);

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('implementation', 'dashboard', navigate, id);

  // 由 Caliper ReadingStarted/ReadingEnded 計算每個節點的閱讀時長（秒）
  // 注意：ReadingStarted/ReadingEnded 事件在學生打開/關閉 ViewNode 對話框時觸發
  // - ReadingStarted: 在 IdeaWall.js 的 handleClickOpen 中觸發（點擊節點打開對話框時）
  // - ReadingEnded: 在 IdeaWall.js 的 handleClose 中觸發（關閉對話框時）
  //   關閉方式包括：點擊「取消」按鈕、點擊對話框外部（backdrop）、按 ESC 鍵
  //   所有關閉方式都會觸發 onClose，進而觸發 handleClose，因此都會記錄 ReadingEnded 事件
  // 
  // 未閉合事件處理：
  // - 所有事件（包括 Started 和 Ended）都會被記錄到資料庫中
  // - 但計算時長時，只有配對成功的事件（Started + Ended）才會被計算
  // - 如果只有 ReadingStarted 沒有 ReadingEnded（例如：用戶直接關閉瀏覽器、網絡斷線），
  //   這個事件會被記錄到資料庫，但不會被計算到閱讀時長中
  // - 這可能導致時長計算略低於實際值，但可以避免異常長時間的記錄
  function computeReadingDurations(events) {
    const byNode = {};
    const stacks = {}; // { nodeId: { actorId: startTime } }
    
    events.forEach(evt => {
      if (evt.event_type !== 'ReadingStarted' && evt.event_type !== 'ReadingEnded') return;
      if (evt.object_type !== 'WallNode' || !evt.object_id) return;
      
      const nodeId = String(evt.object_id);
      const actorId = String(evt.actor_id || evt.Actor?.id || '');
      if (!nodeId || !actorId) return;
      
      const t = new Date(evt.created_at || evt.metadata?.original_timestamp || evt.timestamp).getTime();
      
      if (!byNode[nodeId]) {
        byNode[nodeId] = {
          totalDuration: 0,
          readCount: 0,
          sessions: []
        };
      }
      
      if (!stacks[nodeId]) stacks[nodeId] = {};
      
      if (evt.event_type === 'ReadingStarted') {
        stacks[nodeId][actorId] = t;
      } else if (evt.event_type === 'ReadingEnded') {
        const start = stacks[nodeId][actorId];
        if (start) {
          const d = Math.max(0, Math.floor((t - start) / 1000));
          byNode[nodeId].totalDuration += d;
          byNode[nodeId].readCount += 1;
          byNode[nodeId].sessions.push({ actorId, duration: d });
          delete stacks[nodeId][actorId];
        }
      }
    });
    
    // 注意：stacks 中可能還有未閉合的 ReadingStarted 事件（沒有對應的 ReadingEnded）
    // 這些事件會被記錄到資料庫，但不會被計算到閱讀時長中
    // 可以選擇性地處理這些未閉合事件（例如：使用當前時間或設置最大時長上限）
    
    // 計算平均閱讀時長
    Object.keys(byNode).forEach(nodeId => {
      const node = byNode[nodeId];
      node.averageDuration = node.readCount > 0 ? Math.round(node.totalDuration / node.readCount) : 0;
    });
    
    return byNode;
  }

  // 獲取真實資料
  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const courseDispatchId = id;

        const [detailResp, overviewResp, ideaNodesResp, questionNodesResp, questionsResp, edgesResp, eventsResp] = await Promise.all([
          axiosInstance.get(`${config.getCourseDispatchDetail}${courseDispatchId}/detail`),
          axiosInstance.get(`${config.getTeacherOverview}${courseDispatchId}`),
          axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/idea`),
          axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/question`),
          axiosInstance.get(`${config.getCourseQuestions}${courseDispatchId}`),
          axiosInstance.get(`${config.getNodeEdges}${courseDispatchId}`),
          axiosInstance.get(`api/analytics/events/${courseDispatchId}`)
        ]);

        console.log('[Analytics] API 回應:', {
          detail: { groups: detailResp.data?.data?.taskGroupMatrix?.groups?.length || 0, tasks: detailResp.data?.data?.taskGroupMatrix?.tasks?.length || 0 },
          overview: { recentNodes: overviewResp.data?.data?.recentNodes?.length || 0 },
          ideaNodes: ideaNodesResp.data?.success ? ideaNodesResp.data.data?.length || 0 : 0,
          questionNodes: questionNodesResp.data?.success ? questionNodesResp.data.data?.length || 0 : 0,
          questions: questionsResp.data?.success ? questionsResp.data.data?.length || 0 : 0,
          edges: edgesResp.data?.success ? edgesResp.data.data?.length || 0 : 0,
          events: eventsResp.data?.success ? eventsResp.data.data?.length || 0 : 0
        });

        const detail = detailResp.data?.data || {};
        
        // 設置課程派發資訊
        if (detail.applicationInfo) {
          setCourseDispatch(detail.applicationInfo);
        }
        
        const groups = detail.taskGroupMatrix?.groups || [];
        const tasks = detail.taskGroupMatrix?.tasks || [];
        const matrixStatus = detail.taskGroupMatrix?.matrixStatus || {};
        const overview = overviewResp.data?.data || {};
        const recentNodes = overview.recentNodes || [];
        const ideaNodes = ideaNodesResp.data?.success ? (ideaNodesResp.data.data || []) : [];
        const questionNodes = questionNodesResp.data?.success ? (questionNodesResp.data.data || []) : [];
        const questions = questionsResp.data?.success ? (questionsResp.data.data || []) : [];
        const edges = edgesResp.data?.success ? (edgesResp.data.data || []) : [];
        const allNodes = [...ideaNodes, ...questionNodes];
        const events = eventsResp.data?.success ? (eventsResp.data.data || []) : [];
        
        // 獲取課程派發創建者 ID（老師 ID）
        const courseDispatchCreatorId = detail.applicationInfo?.created_by || 
                                       detailResp.data?.data?.applicationInfo?.created_by ||
                                       detail.created_by || 
                                       detail.courseDispatch?.created_by || 
                                       null;
        
        console.log('[Analytics] 課程派發創建者 ID:', courseDispatchCreatorId);
        console.log('[Analytics] detail 結構:', {
          hasApplicationInfo: !!detail.applicationInfo,
          applicationInfoKeys: detail.applicationInfo ? Object.keys(detail.applicationInfo) : [],
          detailKeys: Object.keys(detail)
        });
        
        console.log('[Analytics] Caliper 事件類型統計:', events.reduce((acc, e) => {
          acc[e.event_type] = (acc[e.event_type] || 0) + 1;
          return acc;
        }, {}));
        // 供圖表使用的簡化節點格式
        const chartNodesData = allNodes.map(n => ({
          author: n.author_name || n.Author?.name || '未知',
          tags: n.node_type === 'information' ? 'information' : (n.node_type || 'idea'),
          content: n.content || '',
          updatedAt: n.updated_at || n.updatedAt || n.created_at || n.createdAt || new Date().toISOString()
        }));

        // 學生名單
        const studentList = groups.flatMap(g => g.members || []);
        studentListRef.current = studentList;
        const totalStudents = studentList.length;

        // 移除「在線與互動」統計（數據不準確，用戶直接關閉分頁時無法偵測）

        // 問題牆待處理統計
        const now = new Date();
        const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);
        const newQuestions = questions.filter(q => {
          const qTime = new Date(q.created_at || q.createdAt || 0);
          return qTime >= tenMinutesAgo && !q.category;
        });
        const toCategorize = questions.filter(q => !q.category && q.is_featured !== true);
        const repliesByQuestion = questionNodes.reduce((acc, n) => {
          if (n.node_type === 'reply' && n.parent_node_id) {
            const key = String(n.parent_node_id);
            acc[key] = (acc[key] || 0) + 1;
          }
          return acc;
        }, {});
        const toReply = questions.filter(q => q.node_type === 'question' && (repliesByQuestion[String(q.id)] || 0) === 0);
        const pendingQuestions = { new: newQuestions.length, toCategorize: toCategorize.length, toReply: toReply.length };

        // 便利貼熱度（想法牆非回覆節點，互動數=連線+回覆+閱讀次數，按互動數排序）
        // 注意：過濾掉老師的任務節點（task_id 存在且 author_id 是課程派發創建者），避免熱度被任務節點佔據
        console.log('[Analytics] 便利貼熱度計算 - 原始數據:');
        console.log('  - 想法牆節點總數:', ideaNodes.length);
        console.log('  - 邊緣總數:', edges.length);
        console.log('  - 所有節點總數:', allNodes.length);
        console.log('  - 課程派發創建者 ID:', courseDispatchCreatorId);
        
        const edgeCountByFrom = edges.reduce((acc, e) => {
          const key = String(e.from_node_id);
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        const replyCountByParent = allNodes.reduce((acc, n) => {
          if (n.parent_node_id) {
            const key = String(n.parent_node_id);
            acc[key] = (acc[key] || 0) + 1;
          }
          return acc;
        }, {});
        
        // 統計每個節點的閱讀次數（ReadingStarted 事件）
        const readCountByNode = events
          .filter(e => e.event_type === 'ReadingStarted' && e.object_type === 'WallNode' && e.object_id)
          .reduce((acc, e) => {
            const nodeId = String(e.object_id);
            acc[nodeId] = (acc[nodeId] || 0) + 1;
          return acc;
        }, {});
        
        console.log('[Analytics] 便利貼熱度計算 - 統計數據:');
        console.log('  - 連線統計 (edgeCountByFrom):', Object.keys(edgeCountByFrom).length, '個節點有連線');
        console.log('  - 回覆統計 (replyCountByParent):', Object.keys(replyCountByParent).length, '個節點有回覆');
        console.log('  - 閱讀統計 (readCountByNode):', Object.keys(readCountByNode).length, '個節點有閱讀記錄');
        
        // 過濾：只顯示學生創建的節點（排除老師的任務節點）
        // 老師的任務節點特徵：有 task_id 且 author_id 是課程派發創建者
        const stickyBase = ideaNodes.filter(n => {
          // 排除回覆節點
          if (n.node_type === 'reply') return false;
          
          // 排除老師的任務節點
          const hasTaskId = !!n.task_id;
          const authorId = String(n.author_id || n.Author?.id || '');
          const isTeacherTask = hasTaskId && courseDispatchCreatorId && authorId === String(courseDispatchCreatorId);
          
          return !isTeacherTask;
        });
        
        console.log('[Analytics] 便利貼熱度計算 - 過濾後（排除老師任務節點）:');
        console.log('  - 學生節點數量:', stickyBase.length);
        console.log('  - 前5個節點詳情:', stickyBase.slice(0, 5).map(n => ({
          id: n.id,
          title: n.title,
          content: n.content ? (n.content.length > 50 ? n.content.substring(0, 50) + '...' : n.content) : '(無內容)',
          author: n.author_name || n.Author?.name || '未知',
          node_type: n.node_type
        })));
        
        const stickyHot = stickyBase
          .map(n => {
            const idStr = String(n.id);
            const edgeCount = edgeCountByFrom[idStr] || 0;
            const replyCount = replyCountByParent[idStr] || 0;
            const readCount = readCountByNode[idStr] || 0;
            const interactions = edgeCount + replyCount + readCount;
            
            // 優先使用title，如果title也沒有或為空，再使用content（移除HTML標籤）
            // 學生可能只用鷹架回復當內容，所以content可能是空的或只有HTML標籤
            let displayContent = '';
            if (n.title && n.title.trim()) {
              displayContent = stripHtml(n.title);
            } else if (n.content && n.content.trim()) {
              displayContent = stripHtml(n.content);
            } else {
              displayContent = '(無內容)';
            }
            
            // 限制長度
            if (displayContent.length > 40) {
              displayContent = displayContent.slice(0, 40) + '...';
            }
            
            const result = {
              id: n.id,
              content: displayContent,
              author: n.author_name || n.Author?.name || '未知',
              interactions,
              edgeCount,
              replyCount,
              readCount,
              // 保存完整內容用於調試
              fullContent: n.content || n.title || '(無內容)',
              fullTitle: n.title || '(無標題)',
              rawContent: n.content,
              rawTitle: n.title
            };
            return result;
          })
          .sort((a, b) => b.interactions - a.interactions)
          .slice(0, 4); // 顯示前 4 筆（因為佔據一半寬度，可以顯示更多）
        
        console.log('[Analytics] 便利貼熱度計算 - 最終結果:');
        console.log('  - Top 3 節點:', stickyHot.map(s => ({
          id: s.id,
          author: s.author,
          interactions: s.interactions,
          edgeCount: s.edgeCount,
          replyCount: s.replyCount,
          readCount: s.readCount,
          content: s.content,
          fullContent: s.fullContent,
          fullTitle: s.fullTitle
        })));

        console.log('[Analytics] 課中即時資料:', {
          問題牆待處理: pendingQuestions,
          便利貼熱度: { 總數: stickyHot.length, 列表: stickyHot.map(s => ({ id: s.id, author: s.author, interactions: s.interactions })) }
        });

        // 跨組查看統計（從 Caliper events 中提取）
        const crossGroupViewEvents = events.filter(e => {
          const hasAction = e.metadata?.action === 'cross_group_view';
          const isReadingStarted = e.event_type === 'ReadingStarted';
          const isIdeaWall = e.tool === 'idea-wall';
          const hasObjectId = e.object_id != null; // 跨組查看應該有 object_id（小組ID）
          
          return hasAction && isReadingStarted && isIdeaWall && hasObjectId;
        });
        
        console.log('[Analytics] 跨組查看事件統計:', {
          總事件數: events.length,
          跨組查看事件數: crossGroupViewEvents.length,
          事件詳情: crossGroupViewEvents.slice(0, 5).map(e => ({
            id: e.id,
            actor_id: e.actor_id,
            object_id: e.object_id,
            metadata: e.metadata,
            created_at: e.created_at
          }))
        });
        
        // 1. 每位學生的跨組查看次數
        const studentViewCounts = {};
        crossGroupViewEvents.forEach(e => {
          const actorId = e.actor_id;
          if (actorId) {
            if (!studentViewCounts[actorId]) {
              studentViewCounts[actorId] = 0;
            }
            studentViewCounts[actorId] += 1;
          }
        });
        
        // 2. 各組被查看的次數（人氣度）
        const groupViewInCounts = {}; // 每組被查看的次數
        crossGroupViewEvents.forEach(e => {
          const groupId = e.object_id;
          if (groupId) {
            groupViewInCounts[groupId] = (groupViewInCounts[groupId] || 0) + 1;
          }
        });
        
        // 跨組查看統計數據
        const crossGroupViewStats = {
          // 1. 每位學生的跨組查看次數
          studentViewCounts: Object.entries(studentViewCounts)
            .map(([actorId, count]) => {
              const student = studentList.find(s => String(s.id) === String(actorId));
              return {
                actorId: parseInt(actorId),
                studentName: student?.name || '未知',
                count
              };
            })
            .sort((a, b) => b.count - a.count),
          
          // 2. 各組被查看的次數（人氣度）
          groupViewInCounts: Object.entries(groupViewInCounts)
            .map(([groupId, count]) => {
              const group = groups.find(g => String(g.id) === String(groupId));
              return {
                groupId: parseInt(groupId),
                groupName: group?.name || group?.groupName || `小組${groupId}`,
                count
              };
            })
            .sort((a, b) => b.count - a.count)
        };

        // 課後：參與概覽（發言/回覆統計）
        const authorAgg = {};
        allNodes.forEach(n => {
          const authorId = n.author_id || n.Author?.id;
          const authorName = n.author_name || n.Author?.name || '未知';
          if (!authorId) return;
          if (!authorAgg[authorId]) {
            authorAgg[authorId] = { id: authorId, name: authorName, posts: 0, replies: 0 };
          }
          if (n.node_type === 'reply') authorAgg[authorId].replies += 1;
          else authorAgg[authorId].posts += 1;
        });
        const durationByActor = computeDurations(events);
        const participation = Object.values(authorAgg)
          .map(s => ({
            id: s.id,
            name: s.name,
            posts: s.posts,
            replies: s.replies,
            duration: durationByActor[s.id] ? Math.round(durationByActor[s.id] / 60) : 0
          }))
          .sort((a, b) => (b.posts + b.replies) - (a.posts + a.replies))
          .slice(0, 10);
        
        console.log('[Analytics] 學生參與概覽:', {
          參與學生數: participation.length,
          時長計算來源: { 事件數: events.filter(e => e.event_type === 'ToolUseStarted' || e.event_type === 'ToolUseEnded').length },
          參與度範例: participation.slice(0, 3).map(s => ({ name: s.name, posts: s.posts, replies: s.replies, duration: s.duration }))
        });

        // 計算小組成員互動網絡（包含老師）
        const networks = {};
        groups.forEach(group => {
          const groupId = group.id;
          const members = group.members || [];
          
          // 分離學生（老師通常不在小組成員列表中）
          const studentMembers = courseDispatchCreatorId 
            ? members.filter(m => String(m.id) !== String(courseDispatchCreatorId))
            : members;
          
          // 所有成員（學生 + 老師，老師單獨添加）
          const allMembers = courseDispatchCreatorId 
            ? [...studentMembers, {
                id: String(courseDispatchCreatorId),
                name: '老師',
                email: null
              }]
            : studentMembers;
          const memberIds = new Set(allMembers.map(m => String(m.id)));
          
          console.log(`[Analytics] 小組 ${groupId} 成員:`, {
            學生數: studentMembers.length,
            老師: courseDispatchCreatorId ? '有' : '無',
            總成員數: allMembers.length
          });
          
          // 初始化互動計數矩陣（包含所有成員，包括老師）
          const interactionMatrix = {};
          allMembers.forEach(m => {
            interactionMatrix[String(m.id)] = {};
            allMembers.forEach(m2 => {
              interactionMatrix[String(m.id)][String(m2.id)] = 0;
            });
          });
          
          // 過濾出屬於該小組的節點（用於互動計算）
          const groupNodes = allNodes.filter(node => {
            const nodeGroupId = node.group_id || node.Group?.id;
            // 屬於該小組的節點，或者沒有 group_id 的節點（可能是跨組節點，但作者是該小組成員）
            return !nodeGroupId || String(nodeGroupId) === String(groupId);
          });
          
          // ====== 互動計算邏輯說明 ======
          // 重要：回覆節點創建時會自動創建一條邊（edge_type = 'reply'）
          // 邊的方向：from_node_id = 父節點（被回覆的節點），to_node_id = 回覆節點
          // 
          // 互動記錄方向：回覆者 -> 被回覆者
          // - 如果 A 回覆了 B 的節點，互動記錄為：A -> B（回覆節點的作者 -> 父節點的作者）
          // - 如果 A 回覆了自己的節點，互動記錄為：A -> A（自己與自己的互動）
          // 
          // 互動類型：
          // 1. 回覆互動：edge_type = 'reply'（回覆節點自動創建的邊）
          // 2. 手動連線：edge_type = 'connection' 或其他（學生手動創建的連線）
          // 
          // 注意：
          // - 回覆節點的 node_type 可以是 "reply", "idea", "question", "info", "experiment"（用戶可選擇的標籤）
          // - 但回覆節點創建的邊的 edge_type 固定是 'reply'
          // - 我們通過邊的 edge_type 來區分回覆和手動連線
          // - 每條邊只計算一次，不會重複計算
          // =================================
          
          // 統一通過邊來計算互動（包含回覆和手動連線）
          edges.forEach(edge => {
            const fromNodeId = String(edge.from_node_id); // 父節點（被回覆的節點）
            const toNodeId = String(edge.to_node_id);     // 回覆節點
            
            const fromNode = groupNodes.find(n => String(n.id) === fromNodeId);
            const toNode = groupNodes.find(n => String(n.id) === toNodeId);
            
            if (fromNode && toNode) {
              const fromAuthorId = String(fromNode.author_id || fromNode.Author?.id); // 被回覆節點的作者
              const toAuthorId = String(toNode.author_id || toNode.Author?.id);       // 回覆節點的作者
              
              // 互動記錄方向：回覆者 -> 被回覆者（toNode 的作者 -> fromNode 的作者）
              // 這樣可以正確記錄「A 回覆了 B」的互動
              if (memberIds.has(toAuthorId) && memberIds.has(fromAuthorId)) {
                // 統一計算：回覆和手動連線都通過邊來計算
                // edge_type = 'reply' → 回覆互動
                // edge_type = 'connection' 或其他 → 手動連線互動
                // 每條邊只計算一次，不會重複計算
                // 注意：互動方向是 toAuthorId -> fromAuthorId（回覆者 -> 被回覆者）
                interactionMatrix[toAuthorId][fromAuthorId] = (interactionMatrix[toAuthorId][fromAuthorId] || 0) + 1;
              }
            }
          });
          
          // 注意：不再單獨計算回覆節點的 parent_node_id，因為：
          // 1. 回覆節點會自動創建邊（edge_type = 'reply'），已經在上面計算了
          // 2. 如果同時計算回覆節點的 parent_node_id 和邊，會重複計算
          // 3. 統一使用邊來計算，可以避免重複計算
          // 4. 互動方向已修正為：回覆者 -> 被回覆者
          
          // 計算每個成員創建的節點數量（只計算該小組的節點）
          const nodeCountByAuthor = {};
          allNodes.forEach(node => {
            const authorId = String(node.author_id || node.Author?.id);
            const nodeGroupId = node.group_id || node.Group?.id;
            
            // 只計算屬於該小組的節點，或者沒有 group_id 的節點（可能是跨組或老師的節點）
            // 對於老師的節點，需要檢查是否屬於該小組的任務節點
            if (memberIds.has(authorId)) {
              // 如果節點有 group_id，必須匹配當前小組
              // 如果節點沒有 group_id，可能是老師創建的任務節點，需要檢查是否分配給該小組
              if (!nodeGroupId || String(nodeGroupId) === String(groupId)) {
                nodeCountByAuthor[authorId] = (nodeCountByAuthor[authorId] || 0) + 1;
              }
            }
          });
          
          // 構建網絡圖數據
          const nodes = allMembers.map(m => {
            const memberId = String(m.id);
            const isTeacher = courseDispatchCreatorId && memberId === String(courseDispatchCreatorId);
            const nodeCount = nodeCountByAuthor[memberId] || 0;
            return {
              id: memberId,
              label: m.name || (isTeacher ? '老師' : `學生${m.id}`),
              title: `${m.name || (isTeacher ? '老師' : '未知')} (ID: ${m.id})\n發送節點: ${nodeCount}個`,
              isTeacher: isTeacher,
              nodeCount: nodeCount
            };
          });
          
          const networkEdges = [];
          Object.keys(interactionMatrix).forEach(fromId => {
            Object.keys(interactionMatrix[fromId]).forEach(toId => {
              const count = interactionMatrix[fromId][toId];
              // 只顯示有互動的邊（包括自己與自己的互動）
              // 自己與自己的互動：當 fromId === toId 時（即回覆者與被回覆者是同一人）
              if (count > 0) {
                const fromMember = allMembers.find(m => String(m.id) === fromId);
                const toMember = allMembers.find(m => String(m.id) === toId);
                const isSelfLoop = fromId === toId; // 自己與自己的互動：從 edge 的 from/to 判斷
                const isTeacherInteraction = (courseDispatchCreatorId && String(fromId) === String(courseDispatchCreatorId)) ||
                                           (courseDispatchCreatorId && String(toId) === String(courseDispatchCreatorId));
                
                networkEdges.push({
                  from: fromId,
                  to: toId,
                  value: count,
                  label: String(count),
                  title: `${fromMember?.name || fromId} → ${toMember?.name || toId}: ${count}次互動${isSelfLoop ? ' (自己與自己)' : ''}`,
                  selfReference: isSelfLoop,
                  isTeacherInteraction: isTeacherInteraction
                });
              }
            });
          });
          
          // 計算總互動次數（所有邊的 value 總和）
          const totalInteractions = networkEdges.reduce((sum, e) => sum + e.value, 0);
          
          // 計算總節點數量（所有成員發送的節點總和）
          const totalNodes = nodes.reduce((sum, n) => sum + (n.nodeCount || 0), 0);
          
          console.log(`[Analytics] 小組 ${groupId} 互動網絡詳情:`, {
            邊數: networkEdges.length,
            總互動次數: totalInteractions,
            總節點數: totalNodes,
            成員節點數: nodes.map(n => ({ id: n.id, name: n.label, nodeCount: n.nodeCount })),
            邊詳情: networkEdges.map(e => ({
              from: e.from,
              to: e.to,
              value: e.value
            }))
          });
          
          networks[groupId] = {
            groupId,
            groupName: group.name || group.groupName || `小組${groupId}`,
            nodes,
            edges: networkEdges,
            interactionMatrix,
            totalMembers: allMembers.length,
            totalStudents: studentMembers.length,
            hasTeacher: !!courseDispatchCreatorId,
            totalInteractions: totalInteractions,
            totalNodes: totalNodes
          };
        });
        
        console.log('[Analytics] 小組互動網絡計算完成:', Object.keys(networks).map(gid => ({
          groupId: gid,
          groupName: networks[gid].groupName,
          members: networks[gid].nodes.length,
          interactions: networks[gid].edges.length
        })));

        // 課後：任務成效
        const taskStats = tasks.map(t => {
          let completedCount = 0;
          let lateCount = 0;
          let inProgressCount = 0;
          let dispatchedCount = 0;
          
          groups.forEach(g => {
            const key = `${t.id}_${g.id}`;
            const status = matrixStatus[key]?.status;
            const submissionContent = matrixStatus[key]?.submission_content;
            
            if (status === 'completed') {
              completedCount += 1;
            } else if (status === 'in_progress') {
              inProgressCount += 1;
            } else if (status === 'dispatched') {
              dispatchedCount += 1;
              // 延遲判斷：已派發但沒有提交內容，且不是剛剛派發的（可以根據時間判斷）
              // 目前簡化：已派發但沒有提交內容就算延遲
              if (!submissionContent) {
                lateCount += 1;
              }
            }
          });
          
          const completedRate = groups.length > 0 ? Math.round((completedCount / groups.length) * 100) : 0;
          
          console.log(`[Analytics] 任務 ${t.id} (${t.title}) 統計:`, {
            總組數: groups.length,
            已完成: completedCount,
            進行中: inProgressCount,
            已派發: dispatchedCount,
            延遲組數: lateCount,
            完成率: `${completedRate}%`
          });
          
          return {
            id: t.id,
            title: t.title,
            completedRate,
            lateGroups: lateCount,
            // 添加詳細統計用於調試
            _stats: {
              total: groups.length,
              completed: completedCount,
              inProgress: inProgressCount,
              dispatched: dispatchedCount,
              late: lateCount
            }
          };
        });

        // 計算閱讀時長分析數據（合併到便利貼熱度中）
        const readingDurations = computeReadingDurations(events);
        
        // 將閱讀時長數據合併到便利貼熱度數據中
        const stickyHotWithReading = stickyHot.map(sticky => {
          const nodeId = String(sticky.id);
          const readingData = readingDurations[nodeId] || {
            totalDuration: 0,
            readCount: 0,
            averageDuration: 0,
            sessions: []
          };
          
          return {
            ...sticky,
            readCount: readingData.readCount,
            totalDuration: readingData.totalDuration,
            averageDuration: readingData.averageDuration
          };
        });

        if (mounted) {
          setRealtimeData({
            pendingQuestions,
            stickyHot: stickyHotWithReading
          });
          setPostClassData({
            participation,
            tasks: taskStats,
            crossGroupViewStats
          });
          setPostLastUpdatedAt(new Date().toISOString());
          setChartNodes(chartNodesData);
          setInteractionNetworks(networks);
          
          // 預設選擇第一個小組
          if (groups.length > 0 && !selectedGroupForNetwork) {
            setSelectedGroupForNetwork(String(groups[0].id));
          }
          
          console.log('[Analytics] 課後診斷資料:', {
            參與概覽: { 學生數: participation.length, 範例: participation.slice(0, 2) },
            任務成效: { 任務數: taskStats.length, 列表: taskStats.map(t => ({ title: t.title, completedRate: t.completedRate, lateGroups: t.lateGroups })) }
          });
        }
      } catch (err) {
        console.error('載入 Analytics 資料失敗:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    
    // 定期刷新數據（每2分鐘），避免過於頻繁
    const refreshInterval = setInterval(() => {
      if (mounted) {
        fetchData();
      }
    }, 120000); // 2分鐘刷新一次
    
    return () => { 
      mounted = false;
      clearInterval(refreshInterval);
    };
  }, [id]);

  // 當資料準備好時渲染圖表
  useEffect(() => {
    if (chartNodes.length > 0 && !loading) {
      // 使用 setTimeout 確保 DOM 已渲染
      const timer = setTimeout(() => {
        try {
          const checks = {
            cumulative: document.querySelector('#cumulative-chart'),
            radar: document.querySelector('#radar-chart'),
            tag: document.querySelector('#tag-chart'),
            authorTag: document.querySelector('#author-tag-chart')
          };
          if (checks.cumulative && checks.radar && checks.tag && checks.authorTag) {
            renderCharts(chartNodes);
          }
        } catch (e) {
          console.error('[Analytics] 圖表渲染失敗:', e);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [chartNodes, loading]);

  // ====== 圖表渲染邏輯 ======
  function renderCharts(nodes) {
    try {
      clearCharts();
    } catch (e) {
      // 圖表尚未初始化時忽略
    }
    
    try {
      renderCumulativeChart(prepareSeries(prepareData(nodes)));
    } catch (e) {
      console.error('[Analytics] renderCumulativeChart 失敗:', e);
    }

    try {
      renderRadarChart(countKeywords(nodes));
    } catch (e) {
      console.error('[Analytics] renderRadarChart 失敗:', e);
    }

    try {
      renderTagChart(nodes);
    } catch (e) {
      console.error('[Analytics] renderTagChart 失敗:', e);
    }

    try {
      renderAuthorTagChart(nodes);
    } catch (e) {
      console.error('[Analytics] renderAuthorTagChart 失敗:', e);
    }
  }

  function clearCharts() {
    const chartIds = ['tag-chart', 'author-tag-chart', 'cumulative-chart', 'radar-chart'];
    chartIds.forEach(id => {
      try {
        ApexCharts.exec(id, 'destroy');
      } catch (e) {
        // 圖表不存在時忽略
      }
    });
  }

  function renderChart(selector, options) {
    const el = document.querySelector(selector);
    if (!el) return null;
    try {
      const chart = new ApexCharts(el, options);
      chart.render();
      return chart;
    } catch (e) {
      console.error(`[Analytics] 圖表渲染失敗: ${selector}`, e);
      return null;
    }
  }

  function renderCumulativeChart(series) {
    if (!series || series.length === 0) return;
    const options = {
      chart: { id: 'cumulative-chart', type: 'line', height: 350 },
      series,
      xaxis: { type: 'datetime', title: { text: 'Updated At' } },
      yaxis: { title: { text: 'Cumulative Nodes' } },
      title: { text: '累計節點時間圖', align: 'center' }
    };
    renderChart('#cumulative-chart', options);
  }

  function renderRadarChart(authorKeywordCount) {
    const categories = ["我的想法", "我覺得更好的想法", "我想知道", "這個想法不能解釋", "舉例和參考來源", "我的總結"];
    const authors = Object.keys(authorKeywordCount);
    if (authors.length === 0) return;
    
    const series = authors.map(author => ({
      name: author,
      data: categories.map(k => {
        const val = authorKeywordCount[author]?.[k] || 0;
        const numVal = Number(val);
        return isNaN(numVal) ? 0 : Math.max(0, Math.ceil(numVal));
      })
    }));
    
    const options = {
      chart: { id: 'radar-chart', type: 'radar', height: 350 },
      series,
      xaxis: { categories },
      yaxis: { labels: { formatter: v => Math.round(v).toString() }, min: 0, tickAmount: 4 },
      dataLabels: { enabled: false },
      title: { text: '鷹架數量圖', align: 'center' }
    };
    renderChart('#radar-chart', options);
  }

  const tagMapping = { idea: '想法', information: '資訊', question: '提問', experiment: '實驗', record: '紀錄', reply: '回覆' };

  function renderTagChart(nodes) {
    const counter = { idea: 0, information: 0, question: 0, experiment: 0, record: 0, reply: 0 };
    nodes.forEach(n => { 
      const tag = n.tags || 'idea';
      counter[tag] = (counter[tag] || 0) + 1;
    });
    const data = [counter.idea, counter.information, counter.question, counter.experiment, counter.record, counter.reply];
    
    const options = {
      chart: { id: 'tag-chart', type: 'bar', height: 350 },
      series: [{ name: '標籤數量', data }],
      xaxis: { categories: ['想法', '資訊', '提問', '實驗', '紀錄', '回覆'] },
      title: { text: '累計標籤圖', align: 'center' }
    };
    renderChart('#tag-chart', options);
  }

  function renderAuthorTagChart(nodes) {
    const authorTagCount = nodes.reduce((acc, n) => {
      const author = n.author || '未知';
      const tag = n.tags || 'idea';
      if (!acc[author]) acc[author] = { idea: 0, information: 0, question: 0, experiment: 0, record: 0, reply: 0 };
      acc[author][tag] = (acc[author][tag] || 0) + 1;
      return acc;
    }, {});
    const authors = Object.keys(authorTagCount);
    if (authors.length === 0) return;
    
    const dataSeries = Object.keys(tagMapping).map(tag => ({ 
      name: tagMapping[tag], 
      data: authors.map(a => authorTagCount[a][tag] || 0) 
    }));
    
    const options = {
      chart: { id: 'author-tag-chart', type: 'bar', height: 350 },
      series: dataSeries,
      xaxis: { categories: authors },
      title: { text: '個人標籤圖', align: 'center' }
    };
    renderChart('#author-tag-chart', options);
  }

  function prepareData(nodes) {
    const sorted = [...nodes].sort((a, b) => {
      try {
        return new Date(a.updatedAt) - new Date(b.updatedAt);
      } catch (e) {
        return 0;
      }
    });
    const authorCum = {}; const seriesData = {};
    sorted.forEach(n => {
      const author = n.author || '未知';
      const updatedAt = n.updatedAt;
      if (!authorCum[author]) { authorCum[author] = 0; seriesData[author] = []; }
      authorCum[author]++;
      try {
        seriesData[author].push({ x: new Date(updatedAt).getTime(), y: authorCum[author] });
      } catch (e) {
        // 日期解析失敗時忽略
      }
    });
    return seriesData;
  }

  function prepareSeries(seriesData) {
    return Object.keys(seriesData).map(author => ({ name: author || '未知', data: seriesData[author] }));
  }

  function countKeywords(nodes) {
    const keywords = ["我的想法", "我覺得更好的想法", "我想知道", "這個想法不能解釋", "舉例和參考來源", "我的總結"];
    const authorKeywordCount = {};
    nodes.forEach(n => {
      const author = n.author || '未知';
      if (!authorKeywordCount[author]) {
        authorKeywordCount[author] = { "我的想法":0, "我覺得更好的想法":0, "我想知道":0, "這個想法不能解釋":0, "舉例和參考來源":0, "我的總結":0 };
      }
      // 同時檢查 content 和 title（因為問題牆的內容可能保存在 title 中）
      const textToCheck = [n.content, n.title].filter(t => t && typeof t === 'string').join(' ');
      if (textToCheck) {
        keywords.forEach(k => { if (textToCheck.includes(k)) authorKeywordCount[author][k]++; });
      }
    });
    return authorKeywordCount;
  }
  // 移除 WebSocket 在線監聽（數據不準確，用戶直接關閉分頁時無法偵測）

  if (loading) {
    return (
      <div className="teacher-layout class-application-analytics-dashboard-container">
        <div className="main-content-v2">
          <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
            currentPhase="implementation"
            currentCourseId={id}
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
            onCourseChange={(newCourseId) => {
              const currentPath = window.location.pathname;
              const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
              navigate(newPath);
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  // 由 Caliper ToolUseStarted/Ended 計算時長（秒）
  // 未閉合事件處理：
  // - 所有事件（包括 Started 和 Ended）都會被記錄到資料庫中
  // - 但計算時長時，只有配對成功的事件（Started + Ended）才會被計算
  // - 如果只有 ToolUseStarted 沒有 ToolUseEnded（例如：用戶直接關閉瀏覽器、網絡斷線），
  //   這個事件會被記錄到資料庫，但不會被計算到使用時長中
  function computeDurations(events) {
    const byActor = {};
    const stacks = {};
    events.forEach(evt => {
      const actorId = evt.actor_id || evt.Actor?.id;
      if (!actorId) return;
      const t = new Date(evt.created_at || evt.metadata?.original_timestamp || evt.timestamp).getTime();
      if (!stacks[actorId]) stacks[actorId] = {};
      if (!byActor[actorId]) byActor[actorId] = 0;
      if (evt.event_type === 'ToolUseStarted') {
        stacks[actorId][evt.tool || 'tool'] = t;
      } else if (evt.event_type === 'ToolUseEnded') {
        const start = stacks[actorId][evt.tool || 'tool'];
        if (start) {
          const d = Math.max(0, Math.floor((t - start) / 1000));
          byActor[actorId] += d;
          delete stacks[actorId][evt.tool || 'tool'];
        }
      }
    });
    // 注意：stacks 中可能還有未閉合的 ToolUseStarted 事件（沒有對應的 ToolUseEnded）
    // 這些事件會被記錄到資料庫，但不會被計算到使用時長中
    return byActor;
  }

  return (
    <div className="teacher-layout class-application-analytics-dashboard-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="implementation"
          currentCourseId={id}
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
          onCourseChange={(newCourseId) => {
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
            navigate(newPath);
          }}
        />

        <div className="class-header-row">
          <h1 className="main-title">
            儀表板
            {courseDispatch && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
                {courseDispatch.title || courseDispatch.activity_name || courseDispatch.lessonPackage || '課程'}
              </Typography>
            )}
          </h1>
        </div>

        <RealtimeView 
          data={realtimeData} 
          participationData={postClassData.participation}
          interactionNetworks={interactionNetworks}
          selectedGroupForNetwork={selectedGroupForNetwork}
          onGroupChange={setSelectedGroupForNetwork}
          crossGroupViewStats={postClassData.crossGroupViewStats}
        />
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, color = 'primary' }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
        <Typography variant="h4" color={`${color}.main`} sx={{ my: 0.5 }}>{value}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children, tooltip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="h6" gutterBottom sx={{ m: 0 }}>{children}</Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="right">
          <IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// 移除 HTML 標籤的輔助函數
function stripHtml(html) {
  if (!html || typeof html !== 'string') return html || '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// 小組成員互動網絡圖組件
function InteractionNetworkGraph({ network }) {
  if (!network || !network.nodes || network.nodes.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 400,
        bgcolor: '#f5f5f5',
        borderRadius: 2,
        border: '1px dashed #ddd'
      }}>
        <Typography variant="body2" color="text.secondary">該小組暫無成員互動數據</Typography>
      </Box>
    );
  }

  // 計算節點大小（根據互動次數）
  const nodeInteractionCounts = {};
  network.edges.forEach(edge => {
    nodeInteractionCounts[edge.from] = (nodeInteractionCounts[edge.from] || 0) + edge.value;
    nodeInteractionCounts[edge.to] = (nodeInteractionCounts[edge.to] || 0) + edge.value;
  });

  const maxInteractionCount = Math.max(...Object.values(nodeInteractionCounts), 1);
  const minNodeSize = 25;
  const maxNodeSize = 50;

  const graphData = {
    nodes: network.nodes.map(node => {
      const interactionCount = nodeInteractionCounts[node.id] || 0;
      const nodeSize = minNodeSize + (interactionCount / maxInteractionCount) * (maxNodeSize - minNodeSize);
      const isTeacher = node.isTeacher || false;
      const nodeCount = node.nodeCount || 0;
      
      // 老師節點使用不同的顏色和大小（更溫和的顏色，框和圖案顏色相同）
      const teacherNodeSize = isTeacher ? 60 : nodeSize;
      const nodeColor = isTeacher 
        ? {
            border: '#B39DDB',
            background: '#B39DDB',
            highlight: {
              border: '#9575CD',
              background: '#9575CD'
            },
            hover: {
              border: '#9575CD',
              background: '#9575CD'
            }
          }
        : {
            border: '#90CAF9',
            background: '#90CAF9',
            highlight: {
              border: '#64B5F6',
              background: '#64B5F6'
            },
            hover: {
              border: '#64B5F6',
              background: '#64B5F6'
            }
          };
      
      // 節點標籤：顯示名稱和發送節點數量
      const nodeLabel = nodeCount > 0 
        ? `${node.label}\n(${nodeCount}個節點)`
        : node.label;
      
      return {
        ...node,
        label: nodeLabel,
        shape: isTeacher ? 'diamond' : 'dot', // 老師使用菱形
        size: teacherNodeSize,
        color: nodeColor,
        font: {
          size: isTeacher ? 16 : 15,
          face: 'Arial, sans-serif',
          color: '#333'
        },
        borderWidth: isTeacher ? 4 : 3,
        shadow: {
          enabled: true,
          color: isTeacher ? 'rgba(156, 39, 176, 0.3)' : 'rgba(0,0,0,0.2)',
          size: isTeacher ? 8 : 5,
          x: isTeacher ? 3 : 2,
          y: isTeacher ? 3 : 2
        }
      };
    }),
    edges: network.edges.map(edge => {
      const isSelfLoop = edge.selfReference || edge.from === edge.to;
      const isTeacherInteraction = edge.isTeacherInteraction || false;
      const edgeWidth = Math.min(Math.max(edge.value * 1.5, 2), 12);
      
      // vis-network 的邊顏色應該是字符串，不是對象（使用更溫和的顏色）
      let edgeColor = '#81D4FA'; // 溫和的藍色 - 學生互動
      if (isSelfLoop) {
        edgeColor = '#EF5350'; // 更明顯的紅色 - 自己與自己（從 #EF9A9A 改為 #EF5350）
      } else if (isTeacherInteraction) {
        edgeColor = '#B39DDB'; // 溫和的紫色 - 與老師的互動
      }
      
      // 自連線的特殊配置：讓它更明顯
      const edgeConfig = {
        ...edge,
        id: edge.id || `${edge.from}-${edge.to}`, // 確保有唯一 ID
        color: edgeColor, // 直接使用字符串顏色
        width: isSelfLoop ? Math.max(edgeWidth * 1.5, 5) : edgeWidth, // 自連線更粗（至少 5px，並加粗 1.5 倍）
        smooth: isSelfLoop ? {
          type: 'curvedCW', // 自連線使用曲線類型，形成明顯的圓圈
          roundness: 0.8 // 更大的圓度，讓圓圈更明顯
        } : {
          type: 'continuous',
          roundness: 0.5
        },
        labelHighlightBold: true,
        font: {
          size: isSelfLoop ? 15 : 13, // 自連線標籤更大
          face: 'Arial, sans-serif',
          color: isSelfLoop ? '#B71C1C' : '#333', // 自連線標籤用深紅色
          strokeWidth: isSelfLoop ? 4 : 2, // 自連線標籤邊框更粗
          strokeColor: '#fff',
          align: 'middle'
        },
        dashes: false, // 不使用虛線
        shadow: isSelfLoop ? {
          enabled: true,
          color: 'rgba(229, 57, 53, 0.3)',
          size: 5,
          x: 2,
          y: 2
        } : false
      };
      
      // 調試：輸出自連線信息
      if (isSelfLoop) {
        console.log(`[Network Graph] 自連線: ${edge.from} -> ${edge.to}, value: ${edge.value}, width: ${edgeConfig.width}, color: ${edgeColor}`);
      }
      
      return edgeConfig;
    })
  };

  const options = {
    layout: {
      improvedLayout: true,
      hierarchical: {
        enabled: false
      },
      randomSeed: 2
    },
    physics: {
      enabled: true,
      stabilization: {
        enabled: true,
        iterations: 250,
        updateInterval: 25
      },
      barnesHut: {
        gravitationalConstant: -3000,
        centralGravity: 0.15,
        springLength: 250,
        springConstant: 0.05,
        damping: 0.12,
        avoidOverlap: 0.8
      },
      repulsion: {
        centralGravity: 0.2,
        springLength: 250,
        springConstant: 0.05,
        nodeDistance: 150,
        damping: 0.12
      }
    },
    interaction: {
      hover: true,
      hoverConnectedEdges: true,
      tooltipDelay: 150,
      zoomView: true,
      dragView: true,
      selectConnectedEdges: true
    },
    nodes: {
      font: {
        size: 15,
        face: 'Arial, sans-serif',
        color: '#333'
      },
      borderWidth: 3,
      borderWidthSelected: 4,
      shadow: {
        enabled: true,
        color: 'rgba(0,0,0,0.2)',
        size: 8,
        x: 3,
        y: 3
      },
      chosen: {
        node: (values, id, selected, hovering) => {
          if (hovering || selected) {
            values.shadow = {
              enabled: true,
              color: 'rgba(74, 144, 226, 0.5)',
              size: 15,
              x: 4,
              y: 4
            };
          }
        }
      }
    },
    edges: {
      arrows: {
        to: {
          enabled: true,
          scaleFactor: 1.2,
          type: 'arrow'
        }
      },
      font: {
        size: 13,
        face: 'Arial, sans-serif',
        color: '#333',
        strokeWidth: 3,
        strokeColor: '#fff',
        align: 'middle'
      },
      labelHighlightBold: true,
      selectionWidth: 3,
      smooth: {
        type: 'continuous',
        roundness: 0.5
      }
    }
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: 600,
      bgcolor: '#fafafa',
      border: '1px solid #e0e0e0',
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* 圖例 */}
      <Box sx={{ 
        position: 'absolute', 
        top: 16, 
        left: 16, 
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        p: 1.5,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
          圖例
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#90CAF9', border: '2px solid #90CAF9' }} />
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>學生</Typography>
        </Box>
        {network.hasTeacher && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, transform: 'rotate(45deg)', bgcolor: '#B39DDB', border: '2px solid #B39DDB' }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>老師</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, bgcolor: '#81D4FA' }} />
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>學生互動</Typography>
        </Box>
        {network.hasTeacher && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 2, bgcolor: '#B39DDB' }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>與老師互動</Typography>
          </Box>
        )}
      </Box>
      
      {/* 統計信息 */}
      <Box sx={{ 
        position: 'absolute', 
        top: 16, 
        right: 16, 
        zIndex: 10,
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        justifyContent: 'flex-end'
      }}>
        <Chip 
          label={`成員: ${network.totalMembers || network.nodes.length}`}
          size="small"
          sx={{ 
            bgcolor: '#E3F2FD', 
            color: '#1976D2',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 28,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }} 
        />
        {network.hasTeacher && (
          <Chip 
            label={`學生: ${network.totalStudents || (network.totalMembers - 1)}`}
            size="small"
            sx={{ 
              bgcolor: '#F3E5F5', 
              color: '#7B1FA2',
              fontWeight: 700,
              fontSize: '0.75rem',
              height: 28,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }} 
          />
        )}
        <Chip 
          label={`節點: ${network.totalNodes || network.nodes.reduce((sum, n) => sum + (n.nodeCount || 0), 0)}個`}
          size="small"
          sx={{ 
            bgcolor: '#FFF3E0', 
            color: '#E65100',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 28,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }} 
        />
        <Chip 
          label={`互動: ${network.totalInteractions || network.edges.reduce((sum, e) => sum + e.value, 0)}次`}
          size="small"
          sx={{ 
            bgcolor: '#E8F5E9', 
            color: '#2E7D32',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 28,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }} 
        />
      </Box>
      <Box sx={{ width: '100%', height: '100%', pt: 1 }}>
        <Graph graph={graphData} options={options} />
      </Box>
    </Box>
  );
}

function RealtimeView({ data, participationData = [], interactionNetworks = {}, selectedGroupForNetwork, onGroupChange, crossGroupViewStats = null }) {
  const rt = data || { pendingQuestions: { new: 0, toCategorize: 0, toReply: 0 }, stickyHot: [] };
  return (
    <Grid container spacing={3}>
      {/* 加入四個圖表（參考 Dashboard.js） */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <SectionTitle tooltip="顯示學生在想法牆的互動趨勢與標籤分布">圖表視覺化</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <div id="cumulative-chart" style={{ height: 360 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <div id="radar-chart" style={{ height: 360 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <div id="tag-chart" style={{ height: 360 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <div id="author-tag-chart" style={{ height: 360 }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* 學生參與概覽 */}
      <Grid item xs={12} md={3}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <SectionTitle tooltip="顯示每位學生的發言數、回覆數和使用時長。資料來源：Caliper Analytics（基於實際節點數據和 ToolUseStarted/Ended 事件配對計算），數據準確可靠">學生參與概覽</SectionTitle>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>學生</TableCell>
                  <TableCell align="right">發言</TableCell>
                  <TableCell align="right">回覆</TableCell>
                  <TableCell align="right">時長(分)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {participationData && participationData.length > 0 ? (
                  participationData.map(s => (
                    <TableRow key={s.id}>
                      <TableCell>{s.name}</TableCell>
                      <TableCell align="right">{s.posts}</TableCell>
                      <TableCell align="right">{s.replies}</TableCell>
                      <TableCell align="right">{Math.round(s.duration)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">暫無參與資料</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      {/* 問題牆待處理 */}
      <Grid item xs={12} md={3}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <SectionTitle tooltip="顯示需要老師處理的問題數量">問題牆待處理</SectionTitle>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography variant="body1" fontWeight="medium">新問題</Typography>
                <Chip color="error" label={rt.pendingQuestions.new || 0} size="medium" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body1" fontWeight="medium">待分類</Typography>
                <Chip color="warning" label={rt.pendingQuestions.toCategorize || 0} size="medium" />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* 便利貼熱度與閱讀時長分析（合併） */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <SectionTitle tooltip="顯示互動最多的便利貼（互動數=連線數+回覆數+閱讀次數）及閱讀時長分析。資料來源：Caliper Analytics（ReadingStarted/ReadingEnded 在 ViewNode 打開/關閉時觸發）">便利貼熱度與閱讀時長</SectionTitle>
            <TableContainer sx={{ maxHeight: 400, mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>內容</TableCell>
                    <TableCell>作者</TableCell>
                    <TableCell align="right">連線</TableCell>
                    <TableCell align="right">回覆</TableCell>
                    <TableCell align="right">閱讀</TableCell>
                    <TableCell align="right">總互動</TableCell>
                    <TableCell align="right">閱讀次數</TableCell>
                    <TableCell align="right">平均時長(秒)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rt.stickyHot && rt.stickyHot.length > 0 ? (
                    rt.stickyHot.map(row => (
                      <TableRow key={row.id}>
                        <TableCell>{stripHtml(row.content)}</TableCell>
                        <TableCell>{row.author}</TableCell>
                        <TableCell align="right">{row.edgeCount || 0}</TableCell>
                        <TableCell align="right">{row.replyCount || 0}</TableCell>
                        <TableCell align="right">{row.readCount || 0}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.interactions}</TableCell>
                        <TableCell align="right">{row.readCount || 0}</TableCell>
                        <TableCell align="right">{row.averageDuration || 0}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">暫無互動</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* 跨組查看統計 */}
      {crossGroupViewStats && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <SectionTitle tooltip="顯示學生和老師跨組查看想法牆的統計數據。資料來源：Caliper Analytics（ReadingStarted 事件，metadata.action='cross_group_view'）">跨組查看統計</SectionTitle>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* 1. 每位學生的跨組查看次數 */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        1. 每位學生的跨組查看次數
                      </Typography>
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>學生</TableCell>
                              <TableCell align="right">查看次數</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {crossGroupViewStats.studentViewCounts && crossGroupViewStats.studentViewCounts.length > 0 ? (
                              crossGroupViewStats.studentViewCounts.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{item.studentName}</TableCell>
                                  <TableCell align="right">{item.count}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={2} align="center">
                                  <Typography variant="body2" color="text.secondary">暫無資料</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 2. 各組被查看的次數（人氣度） */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        2. 各組被查看的次數（人氣度）
                      </Typography>
                      <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>小組</TableCell>
                            <TableCell align="right">被查看次數</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {crossGroupViewStats.groupViewInCounts && crossGroupViewStats.groupViewInCounts.length > 0 ? (
                            crossGroupViewStats.groupViewInCounts.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>{item.groupName}</TableCell>
                                <TableCell align="right">{item.count}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={2} align="center">
                                <Typography variant="body2" color="text.secondary">暫無資料</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}


      {/* 小組成員互動網絡圖 */}
      <Grid item xs={12}>
        <Card sx={{ 
          bgcolor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              pb: 2,
              borderBottom: '2px solid #e3f2fd'
            }}>
              <Box>
                <SectionTitle tooltip="顯示小組內所有成員（學生+老師）之間的互動網絡。連線表示成員之間的互動次數（回覆、連線等），線條粗細代表互動頻率。紫色連線表示與老師的互動。">小組成員互動網絡</SectionTitle>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  節點大小反映互動活躍度，連線粗細表示互動次數。菱形節點為老師，圓形節點為學生
                </Typography>
              </Box>
              <FormControl 
                size="small" 
                sx={{ 
                  minWidth: 220,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#eeeeee'
                    },
                    '&.Mui-focused': {
                      bgcolor: '#fff',
                      boxShadow: '0 0 0 2px rgba(74, 144, 226, 0.2)'
                    }
                  }
                }}
              >
                <InputLabel sx={{ fontWeight: 600 }}>選擇小組</InputLabel>
                <Select
                  value={selectedGroupForNetwork || ''}
                  label="選擇小組"
                  onChange={(e) => onGroupChange && onGroupChange(e.target.value)}
                >
                  {Object.values(interactionNetworks || {}).map(network => (
                    <MenuItem key={network.groupId} value={String(network.groupId)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {network.groupName}
                        </Typography>
                        <Chip 
                          label={`${network.totalMembers || network.nodes.length}人`}
                          size="small"
                          sx={{ 
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#E3F2FD',
                            color: '#1976D2'
                          }}
                        />
                        <Chip 
                          label={`${network.totalInteractions || network.edges.reduce((sum, e) => sum + e.value, 0)}次互動`}
                          size="small"
                          sx={{ 
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#E8F5E9',
                            color: '#2E7D32'
                          }}
                        />
              </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {selectedGroupForNetwork && interactionNetworks[selectedGroupForNetwork] ? (
              <InteractionNetworkGraph network={interactionNetworks[selectedGroupForNetwork]} />
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: 400,
                bgcolor: '#fafafa',
                borderRadius: 2,
                border: '2px dashed #e0e0e0'
              }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  請選擇一個小組
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  查看該小組成員之間的互動網絡
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

    </Grid>
  );
}



